'use client';

import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/AuthContext';
import { loadProgressionState } from '@/lib/progressionService';
import { CALLING_CARDS, AVATARS, TITLES, EMBLEMS, getRarityColor } from '@/lib/cosmetics';
import { ARCHETYPES } from '@/lib/archetypeSystem';
import { getOperatorRank, getRankProgress, getNextOperatorRank } from '@/lib/gamification';
import { intelForLevel } from '@/lib/xpSystem';
import type { DBProgressionState } from '@/lib/db';
import { Shield, Star, Zap, Target, Loader2, Settings, Flame } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { storage } from '@/lib/storage';

interface ProfileCardProps {
  compact?: boolean; // For dashboard use
  showEditLink?: boolean;
  streak?: number; // Consecutive training days
}

export default function ProfileCard({ compact = false, showEditLink = true, streak }: ProfileCardProps) {
  const { user } = useAuth();
  const [progression, setProgression] = useState<DBProgressionState | null>(null);
  const [loading, setLoading] = useState(true);
  const [displayName, setDisplayName] = useState('Operator');
  const [hasNewUnlocks, setHasNewUnlocks] = useState(false);

  useEffect(() => {
    loadData();
  }, [user]);

  // Check for unseen cosmetics unlocks
  useEffect(() => {
    if (!user?.uid || !progression) return;
    try {
      const allUnlocked = [
        ...(progression.unlockedCallingCards || []),
        ...(progression.unlockedEmblems || []),
        ...(progression.unlockedTitles || []),
        ...(progression.unlockedAvatars || []),
      ];
      const seenRaw = storage.get(`seenCosmetics_${user.uid}`);
      const seen: string[] = seenRaw ? JSON.parse(seenRaw) : [];
      const unseen = allUnlocked.filter(id => !seen.includes(id));
      const meaningful = unseen.filter(id => id !== 'default' && id !== 'Recruit');
      setHasNewUnlocks(meaningful.length > 0);
    } catch { /* non-critical */ }
  }, [progression, user?.uid]);

  const loadData = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      const state = await loadProgressionState(user.uid);
      setProgression(state);

      // Get display name from user profile or auth
      const name = user.displayName || storage.get('userName') || 'Operator';
      setDisplayName(name);
    } catch (error) {
      console.error('Failed to load profile data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`${compact ? 'h-32' : 'h-48'} flex items-center justify-center bg-operator-slate rounded-xl border border-operator-steel`}>
        <Loader2 className="w-8 h-8 text-operator-gold animate-spin" />
      </div>
    );
  }

  if (!user || !progression) {
    return (
      <div className={`${compact ? 'h-32' : 'h-48'} flex items-center justify-center bg-operator-slate rounded-xl border border-operator-steel`}>
        <p className="text-gray-400">Sign in to view your profile</p>
      </div>
    );
  }

  // Get equipped cosmetics
  const callingCard = CALLING_CARDS[progression.equippedCallingCard] || CALLING_CARDS['default'];
  const avatar = AVATARS[progression.equippedAvatar] || AVATARS['default'];
  const title = TITLES[progression.equippedTitle] || TITLES['Recruit'];
  const emblem = EMBLEMS[progression.equippedEmblem] || EMBLEMS['default'];
  const archetype = progression.equippedArchetype ? ARCHETYPES[progression.equippedArchetype] : null;

  // Calculate stats
  const operatorScore = progression.archetypeProgress?.['operator']?.operatorScore || 0;
  const totalIntel = progression.lifetimeIntel || 0;
  const totalSessions = progression.archetypeProgress?.['operator']?.totalSessions || 0;

  // Rank progression
  const currentRank = getOperatorRank(operatorScore);
  const nextRank = getNextOperatorRank(operatorScore);
  const rankProgress = getRankProgress(operatorScore);

  // Intel level progression
  const currentLevel = progression.level || 1;
  const levelIntel = progression.levelIntel || 0;
  const intelNeeded = intelForLevel(currentLevel);

  if (compact) {
    // Compact version for dashboard — avionics chrome over calling-card image.
    return (
      <section className="relative rounded-xl overflow-hidden border border-amber-500/30 bg-hud-ink shadow-[0_0_40px_-16px_rgba(245,158,11,0.35)]">
        {/* Calling Card Background — keeps user-customized art */}
        <div className="absolute inset-0">
          <Image
            src={callingCard.image}
            alt={callingCard.name}
            fill
            className="object-cover object-center"
            sizes="(max-width: 768px) 100vw, 600px"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-hud-ink/95 via-hud-ink/80 to-hud-ink/50" />
        </div>

        {/* Corner brackets */}
        <span aria-hidden className="pointer-events-none absolute top-0 left-0 w-3 h-3 border-t border-l border-amber-500/60 z-10" />
        <span aria-hidden className="pointer-events-none absolute top-0 right-0 w-3 h-3 border-t border-r border-amber-500/60 z-10" />
        <span aria-hidden className="pointer-events-none absolute bottom-0 left-0 w-3 h-3 border-b border-l border-amber-500/60 z-10" />
        <span aria-hidden className="pointer-events-none absolute bottom-0 right-0 w-3 h-3 border-b border-r border-amber-500/60 z-10" />

        {/* Classification banner */}
        <header className="relative px-4 py-1.5 border-b border-amber-500/20 flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.28em] z-10">
          <div className="flex items-center gap-2 text-amber-500/90">
            <span className="w-1 h-1 rounded-full bg-hud-cyan animate-hud-blink" />
            <span>Operator File</span>
            <span className="text-slate-700">·</span>
            <span className="text-slate-500">{currentRank.label}</span>
          </div>
          <div className="flex items-center gap-2">
            {hasNewUnlocks && (
              <Link
                href="/barracks"
                className="relative font-mono text-[9px] uppercase tracking-[0.22em] px-1.5 py-0.5 border border-amber-500/50 text-amber-400 hover:bg-amber-500/10 transition"
              >
                <span className="absolute -top-1 -right-1 w-1.5 h-1.5 bg-amber-400 rounded-full animate-hud-blink" />
                New Unlock
              </Link>
            )}
            {showEditLink && (
              <Link
                href="/settings"
                className="p-1 rounded text-slate-500 hover:text-amber-400 transition"
              >
                <Settings className="w-3.5 h-3.5" />
              </Link>
            )}
          </div>
        </header>

        {/* Content */}
        <div className="relative p-4 flex items-center gap-4 z-10">
          {/* Avatar */}
          <div className="relative">
            <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-amber-500 shadow-[0_0_16px_rgba(245,158,11,0.45)]">
              <Image
                src={avatar.image}
                alt={avatar.name}
                width={64}
                height={64}
                className="object-cover w-full h-full"
              />
            </div>
            {/* Emblem Badge */}
            <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-hud-ink border-2 border-amber-500 flex items-center justify-center text-sm overflow-hidden">
              {emblem.icon.startsWith('/') ? (
                <img src={emblem.icon} alt={emblem.name} className="w-full h-full object-cover" />
              ) : emblem.icon}
            </div>
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="font-mono font-bold text-white text-[16px] truncate tracking-tight">{displayName}</span>
              {archetype && (
                <span className="font-mono text-[9px] uppercase tracking-[0.18em] border border-amber-500/40 text-amber-400 px-1.5 py-0.5 whitespace-nowrap">
                  {archetype.icon} {archetype.name}
                </span>
              )}
            </div>
            <div className={`font-mono text-[11px] uppercase tracking-[0.18em] ${getRarityColor(title.rarity).split(' ')[0]}`}>
              {title.name}
            </div>

            {/* Progression Bars */}
            <div className="mt-2 space-y-2">
              {/* Rank progress */}
              <div>
                <div className="flex items-center justify-between mb-1 font-mono text-[9px] uppercase tracking-[0.22em]">
                  <div className="flex items-center gap-1.5">
                    <Target className="w-3 h-3 text-amber-400 flex-shrink-0" aria-hidden />
                    <span className={`font-bold ${currentRank.textClass}`}>{currentRank.label}</span>
                  </div>
                  {nextRank ? (
                    <span className="text-slate-500 tabular-nums">
                      <span className="text-amber-400 font-bold">{rankProgress.pointsToNext}</span> → {nextRank.shortLabel}
                    </span>
                  ) : (
                    <span className="text-amber-400 font-bold">MAX</span>
                  )}
                </div>
                <div className="h-2 bg-slate-900/80 rounded overflow-hidden border border-slate-800">
                  <div
                    className="h-full bg-gradient-to-r from-amber-500/70 to-amber-300 transition-all shadow-[0_0_6px_rgba(245,158,11,0.5)]"
                    style={{ width: `${Math.max(4, Math.round(rankProgress.progress01 * 100))}%` }}
                  />
                </div>
              </div>

              {/* Intel level progress */}
              <div>
                <div className="flex items-center justify-between mb-1 font-mono text-[9px] uppercase tracking-[0.22em]">
                  <div className="flex items-center gap-1.5">
                    <Zap className="w-3 h-3 text-hud-cyan flex-shrink-0" aria-hidden />
                    <span className="font-bold text-hud-cyan">LVL {currentLevel}</span>
                  </div>
                  <span className="text-slate-500 tabular-nums">
                    <span className="text-hud-cyan font-bold">{Math.max(0, intelNeeded - levelIntel)}</span> Intel →
                  </span>
                </div>
                <div className="h-2 bg-slate-900/80 rounded overflow-hidden border border-slate-800">
                  <div
                    className="h-full bg-gradient-to-r from-hud-cyan/60 to-hud-cyan transition-all shadow-[0_0_6px_rgba(103,232,249,0.4)]"
                    style={{ width: `${Math.max(4, Math.round(Math.min(1, levelIntel / Math.max(1, intelNeeded)) * 100))}%` }}
                  />
                </div>
              </div>

              {/* Streak + Sessions */}
              <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.18em] pt-0.5">
                {streak !== undefined && streak > 0 && (
                  <div className="flex items-center gap-1 text-orange-400">
                    <Flame className="w-3 h-3" aria-hidden />
                    <span className="font-bold tabular-nums">{streak}d</span>
                  </div>
                )}
                <div className="flex items-center gap-1 text-emerald-400">
                  <Star className="w-3 h-3" aria-hidden />
                  <span className="font-bold tabular-nums">{totalSessions}</span>
                  <span className="text-slate-500">sessions</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Full version for settings page
  return (
    <div className="relative rounded-xl overflow-hidden border-2 border-operator-gold/50 shadow-xl bg-operator-slate">
      {/* Calling Card Background */}
      <div className="absolute inset-0 bg-operator-gunmetal">
        <Image
          src={callingCard.image}
          alt={callingCard.name}
          fill
          className="object-cover object-center"
          sizes="(max-width: 768px) 100vw, 600px"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/30" />
      </div>

      {/* Content */}
      <div className="relative p-6">
        {/* Top Row: Avatar + Name/Title */}
        <div className="flex items-start gap-5 mb-6">
          {/* Avatar with Emblem */}
          <div className="relative flex-shrink-0">
            <div className="w-24 h-24 rounded-xl overflow-hidden border-3 border-operator-gold shadow-2xl">
              <Image
                src={avatar.image}
                alt={avatar.name}
                width={96}
                height={96}
                className="object-cover w-full h-full"
              />
            </div>
            {/* Emblem Badge */}
            <div className="absolute -bottom-2 -right-2 w-10 h-10 rounded-full bg-operator-slate border-2 border-operator-gold flex items-center justify-center text-xl shadow-lg overflow-hidden">
              {emblem.icon.startsWith('/') ? (
                <img src={emblem.icon} alt={emblem.name} className="w-full h-full object-cover" />
              ) : emblem.icon}
            </div>
          </div>

          {/* Name, Title, Archetype */}
          <div className="flex-1 min-w-0 pt-1">
            <div className="flex items-center gap-3 mb-2">
              <h2 className="font-heading font-bold text-white text-2xl truncate">{displayName}</h2>
            </div>

            {/* Title */}
            <div className={`inline-flex items-center gap-2 text-lg font-semibold mb-3 ${getRarityColor(title.rarity).split(' ')[0]}`}>
              <span>{title.name}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded border ${getRarityColor(title.rarity)}`}>
                {title.rarity.toUpperCase()}
              </span>
            </div>

            {/* Archetype Badge */}
            {archetype && (
              <div className="flex items-center gap-2 bg-operator-gold/20 border border-operator-gold/50 rounded-lg px-3 py-2 w-fit">
                <span className="text-xl">{archetype.icon}</span>
                <div>
                  <div className="text-sm font-bold text-operator-gold">{archetype.name}</div>
                  <div className="text-[10px] text-gray-400 uppercase tracking-wide">{archetype.subtitle}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-black/40 backdrop-blur rounded-lg p-3 border border-operator-steel/30">
            <div className="flex items-center gap-2 mb-1">
              <Target className="w-4 h-4 text-operator-gold" />
              <span className="text-[10px] uppercase tracking-wider text-gray-400">Operator Score</span>
            </div>
            <div className="text-2xl font-heading font-bold text-white">{operatorScore}</div>
          </div>

          <div className="bg-black/40 backdrop-blur rounded-lg p-3 border border-operator-steel/30">
            <div className="flex items-center gap-2 mb-1">
              <Zap className="w-4 h-4 text-blue-400" />
              <span className="text-[10px] uppercase tracking-wider text-gray-400">Lifetime Intel</span>
            </div>
            <div className="text-2xl font-heading font-bold text-white">{totalIntel.toLocaleString()}</div>
          </div>

          <div className="bg-black/40 backdrop-blur rounded-lg p-3 border border-operator-steel/30">
            <div className="flex items-center gap-2 mb-1">
              <Star className="w-4 h-4 text-green-400" />
              <span className="text-[10px] uppercase tracking-wider text-gray-400">Sessions</span>
            </div>
            <div className="text-2xl font-heading font-bold text-white">{totalSessions}</div>
          </div>
        </div>

        {/* Customize Link */}
        {showEditLink && (
          <Link
            href="/barracks"
            className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-operator-gold/20 hover:bg-operator-gold/30 border border-operator-gold/50 rounded-lg text-operator-gold font-semibold text-sm uppercase tracking-wide transition-colors"
          >
            <Shield className="w-4 h-4" />
            Customize in Barracks
          </Link>
        )}
      </div>
    </div>
  );
}
